"use client";

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { auth } from "@/lib/firebase";
import { useQueryClient } from "@tanstack/react-query";

/** Live device state as returned from /api/devices/live */
export type DeviceState = Record<string, unknown> & {
  power?: boolean | string;
  brightness?: number;
  motion?: boolean;
  occupancy?: boolean;
  lux?: number;
  daylight?: number;
  illuminance?: number;
  dimmer_pct?: number;
};

export type DeviceWithState = {
  id: string;
  name: string;
  category?: string | string[];
  subTypes?: string[];      // Casambi: ['dimmer'] | ['daylight'] | ['motion'] | ['color'] etc.
  unitId?: string | number | null;
  spaceId?: string | null;
  state?: DeviceState;
  status?: string;
  model?: string;
};

/** One area (space type "space") with its devices – for "Areas & devices" list (merge by spaceId). */
export type AreaWithDevices = {
  spaceId: string;
  spaceName: string;
  levelIndex: number;
  roomIndex?: number;
  devices: Array<{ id: string; name: string; category?: string | string[]; subTypes?: string[]; unitId?: string | number | null; status?: string; model?: string; state?: DeviceState }>;
};

/** Single space from Spaces API (area with Smplr mapping) */
export type SpaceForMerge = {
  id: string;
  name: string;
  type: string;
  parentId?: string | null;
  metadata?: { smplrLevelIndex?: number; smplrRoomIndex?: number };
};

/** Per-area summary: aggregates + list of devices with live state for tooltip */
export type AreaLiveSummary = {
  lightingLevel: number;
  isOccupied: boolean;
  daylightLevel: number;
  devices: Array<{ id: string; name: string; category?: string | string[]; subTypes?: string[]; unitId?: string | number | null; state?: DeviceState; status?: string; model?: string }>;
};

/** Key = asset id "level-{levelIndex}-room-{roomIndex}" (matches Smplr polygon id) */
export type FloorplanDeviceMap = Record<string, AreaLiveSummary>;

/**
 * Merged record: one map area (polygon) with its space info and devices.
 * Optimized for Smplr map tooltips: use assetId to match polygon, devices for tooltip content.
 */
export type MergedMapTooltipItem = {
  assetId: string;
  spaceId: string;
  spaceName: string;
  levelIndex: number;
  roomIndex: number;
  devices: Array<{ id: string; name: string; category?: string | string[]; state?: DeviceState; status?: string; model?: string }>;
  lightingLevel: number;
  isOccupied: boolean;
  daylightLevel: number;
};

// Fix 4: Standardized background polling to 60s (was 15s) 
// to prevent "request storms" and stay safely within backend rate limits.
const POLL_INTERVAL_MS = 60000;

function joinCat(cat: string | string[] | undefined): string {
  return (Array.isArray(cat) ? cat.join(" ") : String(cat || "")).toLowerCase();
}
function joinSub(sub: string | string[] | undefined): string {
  return (Array.isArray(sub) ? sub.join(" ") : String(sub || "")).toLowerCase();
}

function isLightDevice(cat: string | string[] | undefined, subTypes?: string[]): boolean {
  const c = joinCat(cat);
  const s = joinSub(subTypes);
  // category: 'lighting' covers Casambi dimmer/color/white nodes
  return c.includes("light") || c.includes("led") || c.includes("dimmer") || c.includes("lamp")
    || s.includes("dimmer") || s.includes("white") || s.includes("color") || s.includes("cct") || s.includes("rgbw");
}

function isMotionDevice(cat: string | string[] | undefined, subTypes?: string[]): boolean {
  const c = joinCat(cat);
  const s = joinSub(subTypes);
  return c.includes("motion") || c.includes("occupancy") || c.includes("pir") || c.includes("sensor")
    || s.includes("motion") || s.includes("presence");
}

function isDaylightDevice(cat: string | string[] | undefined, subTypes?: string[]): boolean {
  const c = joinCat(cat);
  const s = joinSub(subTypes);
  // Casambi daylight sensors: category='sensor', subTypes=['daylight']
  return c.includes("daylight") || c.includes("lux") || c.includes("illuminance") || c.includes("light sensor")
    || s.includes("daylight");
}

function getLightingLevel(state: DeviceState | undefined): number {
  if (!state) return 0;
  const on = state.power === true || state.power === "on";
  // brightness > dimmer_pct > dimmer (Casambi raw 0-255) > fallback
  if (typeof state.brightness === "number") return Math.round(state.brightness as number);
  if (typeof state.dimmer_pct === "number") return Math.round(state.dimmer_pct as number);
  if (typeof state.dimmer === "number") {
    const pct = Math.round(((state.dimmer as number) / 255) * 100);
    // If dimmer > 0 treat device as on even if power flag is absent
    return pct > 0 ? pct : (on ? 0 : 0);
  }
  return on ? 100 : 0;
}

function getOccupied(state: DeviceState | undefined): boolean {
  if (!state) return false;
  return state.motion === true || state.occupancy === true;
}

function getLux(state: DeviceState | undefined): number {
  if (!state) return 0;
  const v = state.lux ?? state.daylight ?? state.illuminance;
  return typeof v === "number" ? Math.round(v) : 0;
}

type ContextValue = {
  /** Map by assetId for polygon tooltips (lighting, occupancy, daylight, devices). Real API data only. */
  floorplanDeviceMap: FloorplanDeviceMap;
  /** Merged array: devices + spaces by spaceId, one item per map area, ready for Smplr tooltips. */
  mergedMapTooltips: MergedMapTooltipItem[];
  /** All areas (type "space") with their devices – for "Areas & devices" list (merge by spaceId). */
  allAreasWithDevices: AreaWithDevices[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  /** Instantly reflect a turn-on/turn-off action in the map before the WebSocket echo arrives. */
  optimisticUpdateArea: (assetId: string, action: "on" | "off") => void;
};

const FloorplanDeviceStateContext = createContext<ContextValue>({
  floorplanDeviceMap: {},
  mergedMapTooltips: [],
  allAreasWithDevices: [],
  loading: false,
  error: null,
  refetch: async () => { },
  optimisticUpdateArea: () => { },
});

export function useFloorplanDeviceState() {
  const ctx = useContext(FloorplanDeviceStateContext);
  if (!ctx) throw new Error("useFloorplanDeviceState must be used within FloorplanDeviceStateProvider");
  return ctx;
}

export function FloorplanDeviceStateProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [floorplanDeviceMap, setFloorplanDeviceMap] = useState<FloorplanDeviceMap>({});
  const [mergedMapTooltips, setMergedMapTooltips] = useState<MergedMapTooltipItem[]>([]);
  const [allAreasWithDevices, setAllAreasWithDevices] = useState<AreaWithDevices[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Always-current ref so the WebSocket handler can access latest map without stale closure
  const floorplanDeviceMapRef = useRef<FloorplanDeviceMap>({});
  useEffect(() => { floorplanDeviceMapRef.current = floorplanDeviceMap; }, [floorplanDeviceMap]);

  // Real-time WebSocket updates: listen for device-state-update events dispatched by websocket-context
  useEffect(() => {
    const handleDeviceStateUpdate = (event: Event) => {
      const { deviceId, state } = (event as CustomEvent<{ deviceId: string; state: DeviceState }>).detail;
      const currentMap = floorplanDeviceMapRef.current;

      // Find which assetId polygon contains this device
      let targetAssetId: string | null = null;
      let targetIdx = -1;
      for (const [assetId, summary] of Object.entries(currentMap)) {
        const idx = summary.devices.findIndex((d) => d.id === deviceId);
        if (idx !== -1) { targetAssetId = assetId; targetIdx = idx; break; }
      }
      if (!targetAssetId || targetIdx === -1) return;

      const summary = currentMap[targetAssetId];
      // Merge new state with existing device state so partial updates (e.g. motion-only packet)
      // don't erase previously known values (e.g. daylight lux).
      const updatedDevices = summary.devices.map((d, i) =>
        i === targetIdx ? { ...d, state: { ...(d.state ?? {}), ...state } } : d
      );

      // Recalculate area aggregates
      let lightingLevel = 0, isOccupied = false, daylightLevel = 0;
      for (const d of updatedDevices) {
        const s = (d.state ?? {}) as DeviceState;
        if (isLightDevice(d.category, d.subTypes)) { const L = getLightingLevel(s); if (L > lightingLevel) lightingLevel = L; }
        if (isMotionDevice(d.category, d.subTypes)) { if (getOccupied(s)) isOccupied = true; }
        if (isDaylightDevice(d.category, d.subTypes)) { const lux = getLux(s); if (lux > 0) daylightLevel = lux; }
      }

      setFloorplanDeviceMap((prev) => ({
        ...prev,
        [targetAssetId!]: { lightingLevel, isOccupied, daylightLevel, devices: updatedDevices },
      }));
    };

    if (typeof window !== "undefined") {
      window.addEventListener("device-state-update", handleDeviceStateUpdate);
      return () => window.removeEventListener("device-state-update", handleDeviceStateUpdate);
    }
  }, []); // empty deps — uses ref for current map, so no stale closure

  const optimisticUpdateArea = useCallback((assetId: string, action: "on" | "off") => {
    setFloorplanDeviceMap((prev) => {
      const summary = prev[assetId];
      if (!summary) return prev;

      const updatedDevices = summary.devices.map((d) => {
        if (!isLightDevice(d.category, d.subTypes as string[] | undefined)) return d;
        const s = { ...(d.state ?? {}) } as DeviceState;
        if (action === "on") {
          s.power = true;
          if (typeof s.dimmer_pct === "number" && s.dimmer_pct === 0) s.dimmer_pct = 100;
          if (typeof s.brightness === "number" && s.brightness === 0) s.brightness = 100;
          if (typeof s.dimmer === "number" && s.dimmer === 0) s.dimmer = 255;
        } else {
          s.power = false;
          if (typeof s.dimmer_pct === "number") s.dimmer_pct = 0;
          if (typeof s.brightness === "number") s.brightness = 0;
          if (typeof s.dimmer === "number") s.dimmer = 0;
        }
        return { ...d, state: s };
      });

      let lightingLevel = 0, isOccupied = false, daylightLevel = 0;
      for (const d of updatedDevices) {
        const s = (d.state ?? {}) as DeviceState;
        if (isLightDevice(d.category, d.subTypes as string[] | undefined)) { const L = getLightingLevel(s); if (L > lightingLevel) lightingLevel = L; }
        if (isMotionDevice(d.category, d.subTypes as string[] | undefined)) { if (getOccupied(s)) isOccupied = true; }
        if (isDaylightDevice(d.category, d.subTypes as string[] | undefined)) { const lux = getLux(s); if (lux > 0) daylightLevel = lux; }
      }

      return { ...prev, [assetId]: { lightingLevel, isOccupied, daylightLevel, devices: updatedDevices } };
    });
  }, []);

  const fetchAndMerge = useCallback(async () => {
    // Skip polling if user is not authenticated
    const token = typeof window !== "undefined" ? localStorage.getItem("refreshToken") : null;
    if (!auth.currentUser && !token) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      // Both fetches go through queryClient so they share the React Query cache.
      // staleTime: 30000 prevents hitting the server again if another component
      // fetched the same key within the last 30 seconds.
      const [spacesRes, devicesData] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: ["spaces", { includeInactive: true }],
          queryFn: () => api.get("/api/spaces", { params: { includeInactive: true } }).then(r => r.data),
          staleTime: 30000,
        }),
        queryClient.fetchQuery<{ success?: boolean; devices?: DeviceWithState[] }>({
          queryKey: ["devices", "live"],
          queryFn: () => api.get<{ success?: boolean; devices?: DeviceWithState[] }>("/api/devices/live", { timeout: 8000 }).then(r => r.data),
          staleTime: 30000,
        }).catch(() => ({ devices: [] as DeviceWithState[] })),
      ]);

      const spaces: SpaceForMerge[] = spacesRes?.success && Array.isArray(spacesRes.spaces) ? spacesRes.spaces : [];
      const devices: DeviceWithState[] = Array.isArray(devicesData) ? devicesData : (devicesData?.devices ?? []);

      // Structural types that should never be treated as area polygons
      const SKIP_TYPES = new Set(["building", "floor"]);

      // Build parent→children map so we can collect zone-level devices for each room
      const childIdsByParent = new Map<string, string[]>();
      for (const s of spaces) {
        if (s.parentId) {
          const list = childIdsByParent.get(s.parentId) ?? [];
          list.push(s.id);
          childIdsByParent.set(s.parentId, list);
        }
      }

      // Group devices by the space they are directly assigned to
      const devicesBySpaceId = new Map<string, DeviceWithState[]>();
      for (const device of devices) {
        const sid = device.spaceId ?? null;
        if (sid) {
          const list = devicesBySpaceId.get(sid) ?? [];
          list.push(device);
          devicesBySpaceId.set(sid, list);
        }
      }

      // Helper: get all devices in a space + all its child spaces (zones, etc.)
      const getAllDevicesForSpace = (spaceId: string): DeviceWithState[] => {
        const direct = devicesBySpaceId.get(spaceId) ?? [];
        const childDevs: DeviceWithState[] = [];
        for (const childId of childIdsByParent.get(spaceId) ?? []) {
          childDevs.push(...getAllDevicesForSpace(childId));
        }
        return [...direct, ...childDevs];
      };

      const byAssetId: FloorplanDeviceMap = {};
      const merged: MergedMapTooltipItem[] = [];

      for (const space of spaces) {
        // Skip structural types; require smplr polygon coordinates
        if (
          SKIP_TYPES.has(space.type) ||
          typeof space.metadata?.smplrLevelIndex !== "number" ||
          typeof space.metadata?.smplrRoomIndex !== "number"
        ) continue;

        const levelIndex = space.metadata.smplrLevelIndex;
        const roomIndex = space.metadata.smplrRoomIndex;
        const assetId = `level-${levelIndex}-room-${roomIndex}`;

        // Collect devices assigned directly to the space OR to any child zone
        const devicesInSpace = getAllDevicesForSpace(space.id);

        let lightingLevel = 0;
        let isOccupied = false;
        let daylightLevel = 0;

        for (const d of devicesInSpace) {
          const state = d.state ?? {};
          if (isLightDevice(d.category, d.subTypes)) {
            const L = getLightingLevel(state);
            if (L > lightingLevel) lightingLevel = L;
          }
          if (isMotionDevice(d.category, d.subTypes)) {
            if (getOccupied(state)) isOccupied = true;
          }
          if (isDaylightDevice(d.category, d.subTypes)) {
            const lux = getLux(state);
            if (lux > 0) daylightLevel = lux;
          }
        }

        const deviceList = devicesInSpace.map((d) => ({
          id: d.id,
          name: d.name || "Device",
          category: d.category,
          subTypes: d.subTypes,
          unitId: d.unitId,
          state: d.state,
          status: d.status,
          model: d.model,
        }));

        byAssetId[assetId] = {
          lightingLevel,
          isOccupied,
          daylightLevel: daylightLevel || 0,
          devices: deviceList,
        };

        merged.push({
          assetId,
          spaceId: space.id,
          spaceName: space.name,
          levelIndex,
          roomIndex,
          devices: deviceList,
          lightingLevel,
          isOccupied,
          daylightLevel: daylightLevel || 0,
        });
      }

      // All areas with devices – for "Areas & devices" list (skip structural types)
      const allAreas: AreaWithDevices[] = [];
      for (const space of spaces) {
        if (SKIP_TYPES.has(space.type)) continue;
        const devicesInSpace = getAllDevicesForSpace(space.id);
        const levelIndex = typeof space.metadata?.smplrLevelIndex === "number" ? space.metadata.smplrLevelIndex : 0;
        const roomIndex = typeof space.metadata?.smplrRoomIndex === "number" ? space.metadata.smplrRoomIndex : undefined;
        allAreas.push({
          spaceId: space.id,
          spaceName: space.name,
          levelIndex,
          roomIndex,
          devices: devicesInSpace.map((d) => ({
            id: d.id,
            name: d.name || "Device",
            category: d.category,
            subTypes: d.subTypes,
            unitId: d.unitId,
            state: d.state,
            status: d.status,
            model: d.model,
          })),
        });
      }

      setFloorplanDeviceMap(byAssetId);
      setMergedMapTooltips(merged);
      setAllAreasWithDevices(allAreas);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setFloorplanDeviceMap({});
      setMergedMapTooltips([]);
      setAllAreasWithDevices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAndMerge();
    const t = setInterval(fetchAndMerge, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchAndMerge]);

  const value: ContextValue = {
    floorplanDeviceMap,
    mergedMapTooltips,
    allAreasWithDevices,
    loading,
    error,
    refetch: fetchAndMerge,
    optimisticUpdateArea,
  };

  return (
    <FloorplanDeviceStateContext.Provider value={value}>
      {children}
    </FloorplanDeviceStateContext.Provider>
  );
}
